import { useId, useState } from "react";
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";
import { BarChart3, Table2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ChartContainer, ChartLegend, ChartLegendContent, ChartTooltip, ChartTooltipContent, type ChartConfig } from "@/components/ui/chart";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { GradeKpis, Kpi } from "@/features/overview/telas/Kpi";
import { formatDateBr } from "@/lib/format";
import { useEngajamento, type CelulaMapaCalor, type Engajamento as EngajamentoDados, type Periodo } from "./api";
import { formatNumero, formatPercentual } from "./formato";

// Mesmas categóricas de overview/GraficoLinhas (validadas juntas, superfície branca).
const CONFIG: ChartConfig = {
  dau: { label: "Diários (DAU)", color: "#2a78d6" },
  wau: { label: "7 dias (WAU)", color: "#eb6834" },
  mau: { label: "30 dias (MAU)", color: "#1baf7a" },
};

/** DAU/WAU/MAU, stickiness, mapa de calor e origem — do sistema todo ou de uma instituição. */
export function Engajamento({ periodo, idInstituicao }: { periodo: Periodo; idInstituicao?: number }) {
  const { data, isLoading } = useEngajamento(periodo, idInstituicao);
  const ultimo = data?.serie[data.serie.length - 1];
  const mediaDau = data?.serie.length ? data.serie.reduce((s, p) => s + p.dau, 0) / data.serie.length : undefined;
  const stickiness = mediaDau != null && ultimo?.mau ? mediaDau / ultimo.mau : undefined;

  return (
    <div className="space-y-4">
      <GradeKpis variante="padrao" colunas={4}>
        <Kpi
          variante="padrao"
          rotulo="Usuários ativos em 30 dias (MAU)"
          valor={formatNumero(ultimo?.mau)}
          detalhe={ultimo ? `${formatNumero(ultimo.wau)} nos últimos 7 dias` : undefined}
          isLoading={isLoading}
        />
        <Kpi
          variante="padrao"
          rotulo="Stickiness (DAU médio / MAU)"
          valor={formatPercentual(stickiness)}
          detalhe={mediaDau != null ? `${mediaDau.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} usuários por dia em média` : undefined}
          isLoading={isLoading}
        />
        <Kpi
          variante="padrao"
          rotulo="Logins pelo aplicativo"
          valor={data?.logins ? formatPercentual(data.loginsMobile / data.logins) : "—"}
          detalhe={data ? `${formatNumero(data.loginsMobile)} app · ${formatNumero(data.loginsWeb)} web` : undefined}
          isLoading={isLoading}
        />
        <Kpi
          variante="padrao"
          rotulo="Fora do horário comercial"
          valor={data?.logins ? formatPercentual(data.loginsForaHorario / data.logins) : "—"}
          detalhe="fim de semana ou antes das 8h / depois das 18h"
          isLoading={isLoading}
        />
      </GradeKpis>

      <GraficoEngajamento dados={data} isLoading={isLoading} />
      <MapaCalorAcessos celulas={data?.mapaCalor} isLoading={isLoading} />
    </div>
  );
}

function GraficoEngajamento({ dados, isLoading }: { dados?: EngajamentoDados; isLoading: boolean }) {
  const [tabela, setTabela] = useState(false);
  const tituloId = useId();
  const pontos = (dados?.serie ?? []).map((p) => ({ ...p, rotulo: formatDateBr(p.dia).slice(0, 5) }));

  return (
    <section aria-labelledby={tituloId} className="rounded-xl border border-border bg-card p-5 shadow-card">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 id={tituloId} className="text-base font-semibold text-foreground">
          Usuários distintos por dia
        </h2>
        <Button variant="ghost" size="sm" onClick={() => setTabela((t) => !t)}>
          {tabela ? <BarChart3 className="h-4 w-4" /> : <Table2 className="h-4 w-4" />}
          <span className="ml-1.5">{tabela ? "Gráfico" : "Tabela"}</span>
        </Button>
      </div>
      {isLoading ? (
        <Skeleton className="h-72 w-full" />
      ) : tabela ? (
        <div className="max-h-72 overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Dia</TableHead>
                <TableHead className="text-right">DAU</TableHead>
                <TableHead className="text-right">WAU</TableHead>
                <TableHead className="text-right">MAU</TableHead>
                <TableHead className="text-right">DAU/MAU</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pontos.map((p) => (
                <TableRow key={p.dia}>
                  <TableCell>{formatDateBr(p.dia)}</TableCell>
                  <TableCell className="text-right tabular-nums">{p.dau}</TableCell>
                  <TableCell className="text-right tabular-nums">{p.wau}</TableCell>
                  <TableCell className="text-right tabular-nums">{p.mau}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatPercentual(p.stickiness)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <ChartContainer config={CONFIG} className="aspect-auto h-72 w-full">
          <LineChart data={pontos} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <XAxis dataKey="rotulo" tickLine={false} axisLine={false} tickMargin={8} minTickGap={24} />
            <YAxis tickLine={false} axisLine={false} width={40} allowDecimals={false} />
            <ChartTooltip cursor={{ strokeDasharray: "3 3" }} content={<ChartTooltipContent />} />
            <ChartLegend verticalAlign="top" content={<ChartLegendContent />} />
            {(["mau", "wau", "dau"] as const).map((k) => (
              <Line key={k} dataKey={k} type="monotone" stroke={`var(--color-${k})`} strokeWidth={2} dot={false} isAnimationActive={false} />
            ))}
          </LineChart>
        </ChartContainer>
      )}
    </section>
  );
}

const DIAS_SEMANA = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const HORAS = Array.from({ length: 24 }, (_, h) => h);

/** Logins por dia da semana × hora: mostra o turno que realmente usa o sistema. */
export function MapaCalorAcessos({ celulas, isLoading }: { celulas?: CelulaMapaCalor[]; isLoading: boolean }) {
  const tituloId = useId();
  const grade = new Map<string, number>();
  let maximo = 0;
  for (const c of celulas ?? []) {
    grade.set(`${c.diaSemana}-${c.hora}`, c.logins);
    maximo = Math.max(maximo, c.logins);
  }

  return (
    <section aria-labelledby={tituloId} className="rounded-xl border border-border bg-card p-5 shadow-card">
      <h2 id={tituloId} className="mb-3 text-base font-semibold text-foreground">
        Logins por dia da semana e hora
      </h2>
      {isLoading ? (
        <Skeleton className="h-48 w-full" />
      ) : maximo === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">Nenhum login no período.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] border-separate border-spacing-0.5 text-xs" role="grid">
            <thead>
              <tr>
                <th className="w-10" />
                {HORAS.map((h) => (
                  <th key={h} scope="col" className="font-normal text-muted-foreground">
                    {h % 3 === 0 ? `${h}h` : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {DIAS_SEMANA.map((dia, i) => (
                <tr key={dia}>
                  <th scope="row" className="pr-2 text-left font-normal text-muted-foreground">
                    {dia}
                  </th>
                  {HORAS.map((h) => {
                    const n = grade.get(`${i + 1}-${h}`) ?? 0;
                    return (
                      <td
                        key={h}
                        className="h-6 rounded-sm"
                        style={{ background: n ? `rgba(42, 120, 214, ${0.12 + 0.88 * (n / maximo)})` : "hsl(var(--muted))" }}
                        title={`${dia} ${h}h: ${formatNumero(n)} login(s)`}
                        aria-label={`${dia} ${h}h: ${n} login(s)`}
                      />
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-2 text-xs text-muted-foreground">Mais escuro = mais logins (máximo {formatNumero(maximo)} numa célula).</p>
        </div>
      )}
    </section>
  );
}
